// noinspection ES6PreferShortImport

import type { VNode } from "preact";
import type { HostEntry } from "../types";
import {fmtPing} from "@/lib/strings";
type Props = {
  host: HostEntry;
  preferred: string | null;
  myBiasTotal: number;
  onBoost: (domain: string, delta: number) => void;
  onPenalize: (domain: string, delta: number) => void;
  onPrefer: (domain: string) => void;
};
function fmtAgo(iso: string | null): string {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
function rowClass(host: HostEntry): string {
  if (!host.healthy) return "row row--danger";
  if (host.effective >= 75) return "row row--healthy";
  if (host.effective >= 50) return "row row--warning";
  return "row row--danger";
}
function fmtSigned(v: number): string {
  return v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1);
}
/**
 * Status badge. Only rendered when it adds information the colors don't already carry. Healthy rows get nothing: the green accent bar and the green score already say it. Shown inline next to the domain.
 */
function statusBadge(host: HostEntry): VNode | null {
  if (!host.healthy)
    return (<span class="badge badge--danger"><span class="badge__dot" /> Down</span>);
  if (host.effective >= 50 && host.effective < 75)
    return (<span class="badge badge--warning"><span class="badge__dot" /> Slow</span>);
  if (host.effective < 50)
    return (<span class="badge badge--danger"><span class="badge__dot" /> Poor</span>);
  return null;
}
// noinspection GrazieInspection
export function HostRow({ host, preferred, myBiasTotal, onBoost, onPenalize, onPrefer }: Props): VNode {
  // bias is -50..+50, render a centered bar.
  const biasPct = Math.abs(host.bias) / 50; // 0..1
  const biasLeft = host.bias >= 0 ? 50 : 50 - biasPct * 50;
  const biasWidth = biasPct * 50;
  const isPreferred = preferred === host.domain;
  return (
    <article class={rowClass(host)} data-domain={host.domain}><div class="row-inner">
      <div class="row__score" data-tip="Effective score, 0–100: observed health (EWMA of uptime and latency) plus the shared priority adjustment. This drives how often the instance is picked." data-tipkey="row-score"><span class="row__score-value">{host.effective.toFixed(0)}</span><span class="row__score-caption">/100</span></div>
      {/* Identity */}
      <div class="row__id">
        <div class="row__id-line">
          <a class="row__domain" href={`/v/?domain=${encodeURIComponent(host.domain)}`} data-tip={`View Twitter/X through ${host.domain} in the embedded viewer. Shift-click (or the ↗ icon) opens the instance's own site in a new tab.`} data-tipkey="row-view">
            {host.domain}
          </a>
          <a class="row__ext" href={host.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${host.url} in a new tab`} data-tip={`Open the ${host.domain} website directly in a new tab.`} data-tipkey="row-ext">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7" /><path d="M8 7h9v9" /></svg>
          </a>
          {statusBadge(host)}
          {isPreferred && (
            <span
              class="badge badge--pref"
              data-tip="This is your preferred instance. It will be used while it stays healthy. Un-prefer it any time with the ★ button."
              data-tipkey="row-preferred-flag">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round">
                <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              Preferred
            </span>
          )}
        </div>
        <div class="row__sub">
          {host.rss && <span>RSS</span>}
          {host.blocks_iframe && (
            <span data-tip="This instance sends X-Frame-Options or CSP frame-ancestors, so it can't be embedded directly — the /v resolver will route it through the /proxy." data-tipkey="row-noiframe">
              no iframe
            </span>
          )}
          {host.version_state && host.version_state !== "running" && (
            <span>{host.version_state}</span>
          )}
          {host.source_healthy_pct != null && (
            <span>source {host.source_healthy_pct.toFixed(0)}%</span>
          )}
          {!host.healthy && host.consecutive_failures > 0 && (
            <span class="row__sub-fail">
              {host.consecutive_failures} consecutive failure
              {host.consecutive_failures === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
      {/* Compact stats */}
      <div class="row__stat row__stat--lead" data-tip={`Average response time from the uptime poller: ${host.ping_avg != null ? `${Math.round(host.ping_avg)} ms` : "no data"}.`} data-tipkey="row-ping">
        <span class="row__stat-label">Ping</span>
        <span class="row__stat-value">{fmtPing(host.ping_avg)}</span>
      </div>
      <div class="row__stat" data-tip="Number of polls observed so far and the current consecutive-failure streak." data-tipkey="row-samples">
        <span class="row__stat-label">Polls</span>
        <span class="row__stat-value">
          {host.samples}
          {host.consecutive_failures > 0 && (<em class="row__stat-em"> · {host.consecutive_failures} fail</em>)}
        </span>
      </div>
      <div class="row__stat" data-tip={host.last_healthy ? `Last seen healthy ${fmtAgo(host.last_healthy)} ago.` : "This instance has never been observed healthy."} data-tipkey="row-lastok">
        <span class="row__stat-label">Last ok</span>
        <span class="row__stat-value">{fmtAgo(host.last_healthy)}</span>
      </div>
      {/* Priority (server-side ranking bias) */}
      <div
        class="row__bias"
        data-tip={`Priority ${fmtSigned(host.bias)} — the server's shared nudge (called “bias” in the API) merged with your own saved adjustments: your boosts act as a personal floor, your penalties as a personal ceiling, so your view survives drift and other visitors' changes. The shared value alone decides /v picking; Boost/Penalize move it by ±10 and it slowly drifts back to neutral (0).${
          myBiasTotal !== 0
            ? ` Your own adjustments total ${myBiasTotal > 0 ? "+" : ""}${myBiasTotal} and are saved in this browser's local storage.`
            : " Boost/Penalize below to move it — your adjustments are also recorded in this browser."
        }`}
        data-tipkey="row-bias">
        <div class="bias-bar__track">
          <div class="bias-bar__center" />
          <div class={`bias-bar__fill${host.bias < 0 ? " bias-bar__fill--neg" : ""}`} style={`left: ${biasLeft}%; width: ${biasWidth}%;`}/>
        </div>
        <div class="row__bias-nums">
          <span>
            <span class="row__bias-word">priority </span>
            {fmtSigned(host.bias)}
          </span>
          {myBiasTotal !== 0 && (
            <span class="row__bias-you">
              you {myBiasTotal > 0 ? "+" : ""}
              {myBiasTotal}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div class="row__actions">
        <button class="btn btn--sm btn--boost" onClick={() => onBoost(host.domain, 10)} data-tip="Boost: adds +10 to this instance's preference, making it more likely to be selected." data-tipkey="act-boost">
          <span class="btn__sign">＋</span> Boost
        </button>
        <button class="btn btn--sm btn--penalize" onClick={() => onPenalize(host.domain, -10)} data-tip="Penalize: subtracts 10 from  this instance's preference, making it less likely to be selected." data-tipkey="act-penalize">
          <span class="btn__sign">−</span> Penalize
        </button>
        <button class={`btn btn--sm btn--star${isPreferred ? " btn--star-active" : ""}`} onClick={() => onPrefer(host.domain)} aria-pressed={isPreferred}
          data-tip={isPreferred
              ? "This is your preferred instance. It will be used automatically while it's considered healthy. Click to un-prefer it."
              : "Make this your preferred instance to use as long as it stays healthy, instead of picking an instance at random."
          } data-tipkey="act-prefer">
          <svg width="13" height="13" viewBox="0 0 24 24" fill={isPreferred ? "currentColor" : "none"} stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
          {isPreferred ? "Preferred" : "Prefer"}
        </button>
      </div></div></article>
  );
}
